import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listOutcomes, listProjects, updateOutcomeListing, type Outcome, type Project } from '@/api';
import { categoryIcon, formatPrice, listingStats, toAuthorPlaybook, type AuthorPlaybook } from '@/pages/marketplace/mapPlaybook';

type StatusFilter = 'all' | 'published' | 'draft';

function PriceField({ valueCents, onSave, busy }: { valueCents: number; onSave: (cents: number) => void; busy?: boolean }) {
  const [text, setText] = useState(valueCents === 0 ? '' : String(valueCents / 100));
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setText(valueCents === 0 ? '' : String(valueCents / 100));
  }, [valueCents]);

  const save = () => {
    const n = parseFloat(text);
    onSave(Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1 bg-surface2 border border-border rounded-lg px-3 py-1.5">
        <span className="text-sm text-texts">$</span>
        <input
          value={text}
          onChange={(e) => setText(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0"
          inputMode="decimal"
          aria-label="Price in dollars"
          className="w-14 bg-transparent text-sm font-mono text-textp outline-none"
        />
      </div>
      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="whitespace-nowrap text-xs font-medium text-accent-text bg-accent hover:bg-accent-hover disabled:opacity-60 px-3 py-1.5 rounded-lg transition-colors"
      >
        {saved ? 'Saved' : 'Set price'}
      </button>
    </div>
  );
}

function PlaybookPricingCard({
  playbook,
  onPrice,
  onToggle,
  busy,
}: {
  playbook: AuthorPlaybook;
  onPrice: (id: string, cents: number) => void;
  onToggle: (id: string, published: boolean) => void;
  busy?: boolean;
}) {
  const icon = categoryIcon(playbook.category);
  const published = playbook.published;

  return (
    <div className="bg-surface border border-border rounded-xl p-5" data-testid="author-playbook-card">
      <div className="flex items-start justify-between gap-3">
        <span className="w-10 h-10 shrink-0 rounded-lg bg-accentSoft border border-accentLine flex items-center justify-center">
          <i className={`${icon} text-accent text-lg`} />
        </span>
        <span
          className={`whitespace-nowrap inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${
            published
              ? 'bg-accentSoft text-accent border border-accentLine'
              : 'bg-surface2 text-texts border border-border'
          }`}
        >
          <i className={published ? 'ri-checkbox-circle-line' : 'ri-draft-line'} />
          {published ? 'Published' : 'Draft'}
        </span>
      </div>

      <h3 className="mt-4 text-base font-semibold text-textp">{playbook.name}</h3>
      <p className="mt-1 text-sm text-texts line-clamp-2 leading-relaxed">
        {playbook.description || 'No description recorded.'}
      </p>

      <div className="mt-3 flex items-center gap-3 text-xs text-texts flex-wrap">
        <span className="whitespace-nowrap inline-flex items-center gap-1">
          <i className="ri-scales-3-line text-success" />
          {playbook.score.toFixed(1)}/12
        </span>
        <span className="whitespace-nowrap inline-flex items-center gap-1">
          <i className="ri-git-branch-line" />
          {playbook.steps} steps
        </span>
        {published && (
          <span className="whitespace-nowrap inline-flex items-center gap-1">
            <i className="ri-download-2-line" />
            {playbook.downloads} downloads
          </span>
        )}
        <span className="whitespace-nowrap inline-flex items-center gap-1 font-medium text-textp">
          {formatPrice(playbook.priceCents)}
        </span>
      </div>

      <div className="mt-5 pt-4 border-t border-border">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-widest text-texts">Price</span>
          {playbook.projectId && (
            <Link to={`/studio?project=${playbook.projectId}`} className="text-xs text-texts hover:text-textp">
              Open in Studio
            </Link>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          <PriceField valueCents={playbook.priceCents} onSave={(n) => onPrice(playbook.id, n)} busy={busy} />
          <button
            type="button"
            data-testid="publish-toggle"
            onClick={() => onToggle(playbook.id, !published)}
            disabled={busy}
            className={`whitespace-nowrap inline-flex items-center gap-1.5 text-sm font-medium px-3.5 py-1.5 rounded-lg transition-colors disabled:opacity-60 ${
              published
                ? 'bg-surface2 hover:bg-border text-textp border border-border'
                : 'text-accent-text bg-accent hover:bg-accent-hover'
            }`}
          >
            <i className={published ? 'ri-eye-off-line' : 'ri-earth-line'} />
            {published ? 'Unpublish' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlaybooksView() {
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');

  useEffect(() => {
    Promise.all([listOutcomes(), listProjects()])
      .then(([os, ps]) => {
        setOutcomes(Array.isArray(os) ? os : []);
        setProjects(Array.isArray(ps) ? ps : []);
        setErr(null);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'failed'))
      .finally(() => setLoading(false));
  }, []);

  const playbooks = useMemo(() => {
    const byOutcome = new Map<string, string>();
    for (const p of projects) {
      if (p.outcome_id) byOutcome.set(p.outcome_id, p.id);
    }
    return outcomes.map((o) => toAuthorPlaybook(o, byOutcome.get(o.id)));
  }, [outcomes, projects]);

  const stats = useMemo(() => listingStats(playbooks), [playbooks]);
  const visible = playbooks.filter((p) => filter === 'all' || (filter === 'published' ? p.published : !p.published));

  const patchListing = async (id: string, body: { is_public?: boolean; price_cents?: number }) => {
    setErr(null);
    setBusyId(id);
    try {
      const updated = await updateOutcomeListing(id, body);
      setOutcomes((prev) => prev.map((o) => (o.id === id ? { ...o, ...updated } : o)));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-textp">My playbooks</h1>
          <p className="text-sm text-texts mt-1">Set prices and publish playbooks to the marketplace.</p>
        </div>
        <Link
          to="/marketplace"
          className="whitespace-nowrap inline-flex items-center gap-1.5 text-sm font-medium text-accent-text bg-accent hover:bg-accent-hover px-4 py-2 rounded-lg transition-colors"
        >
          <i className="ri-store-2-line" />
          Open marketplace
        </Link>
      </div>

      {err && (
        <div className="mt-4 text-sm text-error bg-error/10 border border-error/30 rounded-md px-3 py-2">{err}</div>
      )}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-surface border border-border rounded-xl p-4">
          <div className="text-xs uppercase tracking-widest text-texts">Published</div>
          <div className="mt-2 text-2xl font-mono text-textp">{stats.published}</div>
        </div>
        <div className="bg-surface border border-border rounded-xl p-4">
          <div className="text-xs uppercase tracking-widest text-texts">Drafts</div>
          <div className="mt-2 text-2xl font-mono text-textp">{stats.drafts}</div>
        </div>
        <div className="bg-surface border border-border rounded-xl p-4">
          <div className="text-xs uppercase tracking-widest text-texts">Est. earnings</div>
          <div className="mt-2 text-2xl font-mono text-textp">${stats.earnings}</div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-1 bg-surface2 border border-border rounded-full p-1 w-fit">
        {(['all', 'published', 'draft'] as StatusFilter[]).map((f) => {
          const active = filter === f;
          return (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`whitespace-nowrap text-sm px-4 py-1.5 rounded-full transition-colors capitalize ${
                active ? 'bg-accent text-accent-text' : 'text-texts hover:text-textp'
              }`}
            >
              {f}
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-texts">Loading your playbooks…</p>
      ) : visible.length === 0 ? (
        <div className="mt-8 bg-surface border border-border rounded-xl p-12 text-center">
          <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-xl bg-accentSoft border border-accentLine">
            <i className="ri-price-tag-3-line text-accent text-2xl" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-textp">No playbooks here</h2>
          <p className="mt-1 text-sm text-texts">
            {playbooks.length === 0
              ? 'Create a playbook in Studio, then come back to set a price and publish it.'
              : filter === 'draft'
                ? 'All your playbooks are published.'
                : 'Publish a playbook to see it here.'}
          </p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {visible.map((p) => (
            <PlaybookPricingCard
              key={p.id}
              playbook={p}
              onPrice={(id, cents) => patchListing(id, { price_cents: cents })}
              onToggle={(id, published) => patchListing(id, { is_public: published })}
              busy={busyId === p.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
